import React from 'react'
import { CurrencyFormatter } from 'helpers/formatters'
import dayjs from 'dayjs'

import {
  BenefitRow,
  MonthlyPayment,
  MonthlyTotal,
} from '../../../api/reconciliation-v2-api-client'
import { colors, typography } from '../tokens'
import { buildPeriodKey, PROVIDER_PILL } from './helpers'
import {
  computePeriodMoneySummary,
  summarizeCellRefunds,
} from '../recon-v2-helpers'
import {
  CountBadge,
  PaymentTypeChip,
  ProviderPill,
  ProviderPillVariant,
  RefundMark,
} from './pills'
import { HistorySvg } from './icons'
import styles from './BenefitsTable.module.scss'

/**
 * A single month <td>. Imports the SHARED BenefitsTable.module.scss so its
 * hashed `.mv`/`.warn`/`.mzero`/`.hatch`/`.cell-history` classes match the rest
 * of the table (see hard rule #1 in that stylesheet's header). Do NOT give this
 * component its own table stylesheet.
 */

const currencyFormatter = new CurrencyFormatter()

/**
 * Map a payment source to its provider-pill color variant.
 *
 * NOTE: no enum matched the compared values — `source` is a free-form string
 * union ('marqeta' | 'wex' | 'i_stream' | 'check' | 'group_ach' | 'ee_paid')
 * on the API payment DTO, not the `PeriodStatus` enum, so the cases stay as
 * string literals.
 */
const providerVariant = (source: string): ProviderPillVariant => {
  switch (source) {
    case 'marqeta':
      return 'marqeta'
    case 'wex':
      return 'wex'
    default:
      return 'neutral'
  }
}

interface MonthCellProps {
  row: BenefitRow
  mi: number
  year: number
  benefitLabel: string
  showDetails: boolean
  outOfRange: boolean
  coverageTitle: string
  onAdjust: (row: BenefitRow, periodKey: string) => void
  onOpenActivity: (args: {
    benefitId: string
    benefitLabel: string
    month: string
  }) => void
}

const MonthCell: React.FC<MonthCellProps> = ({
  row,
  mi,
  year,
  benefitLabel,
  showDetails,
  outOfRange,
  coverageTitle,
  onAdjust,
  onOpenActivity,
}) => {
  const periodKey = buildPeriodKey(year, mi)

  const total: MonthlyTotal | undefined = row.monthlyTotals[periodKey]
  const payments: MonthlyPayment[] = total?.payments ?? []

  // An empty out-of-coverage month used to render here as a full-bleed hatch
  // with no onClick — which made the Adjust drawer UNREACHABLE for exactly the
  // months an operator now needs to reach (a binder payment before the
  // effective date, a carrier billing after termination). Allocating out of
  // window is legal, so that branch is gone and the cell falls through to the
  // zero branch below: still clickable, still marked `pastCovered` so the
  // "nothing was owed here" cue survives. Un-gate the INTERACTION, not the
  // signal. See docs/adr/0004 in the enrollment-platform repo.
  //
  // Removing it also retired the `hasCreditOnPeriod` escape hatch, which
  // existed only to stop that branch swallowing a credited month: a credit
  // outlives the allocation that licensed it, so hiding the month made the debt
  // invisible AND unreachable. Every month is reachable now, so nothing needs
  // to be exempted from hiding.

  // payment_assignment payment type for this benefit-month, shown as a chip
  // regardless of whether a live payment has been allocated yet.
  const paymentType = total?.paymentType ?? null

  // In coverage, NO transaction: leave the amount blank (a $0 sum only shows
  // when there are actual transactions that net to zero — that's the populated
  // branch below). Still clickable to adjust and still carries the history
  // button (a month with no live payment can have events, e.g. a payment that
  // was allocated then removed).
  if (payments.length === 0) {
    const hasActivityZero = !!total?.hasActivity
    // A credit outlives the allocation that licensed it: deallocating a
    // period's last payment leaves the debt to the member standing, and the
    // server keeps sending credited/netDue for the cell. Without this the strip
    // would drop the whole money summary while the Adjust drawer below still
    // lists the credits — the grid-vs-drawer disagreement this feature's
    // lockstep comments exist to prevent.
    const zeroMoney = computePeriodMoneySummary({
      totalPaid: 0,
      expected: total?.expected ?? null,
      creditAmounts: total?.credited ? [total.credited] : [],
      netDue: total?.netDue,
    })
    return (
      <td
        className={`${styles.mcol} ${styles.mzero}${
          outOfRange ? ` ${styles.pastCovered}` : ''
        }`}
        title={
          outOfRange ? `${coverageTitle} — adjust payment` : 'Adjust payment'
        }
        style={{ cursor: 'pointer' }}
        onClick={() => onAdjust(row, periodKey)}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: paymentType ? 'space-between' : 'flex-end',
            gap: 4,
            width: '100%',
          }}
        >
          {paymentType && <PaymentTypeChip type={paymentType} />}
          <button
            type="button"
            className={`${styles.cellActivityBtn} ${styles['cell-history']}${
              hasActivityZero ? ` ${styles['has-activity']}` : ''
            }`}
            title="Activity log for this month"
            onClick={(e) => {
              e.stopPropagation()
              onOpenActivity({
                benefitId: row.benefitId,
                benefitLabel,
                month: periodKey,
              })
            }}
          >
            <HistorySvg size={13} />
          </button>
        </div>
        {/* Only on a credited month — an uncredited empty cell stays blank, as
            it always has. No paid amount is printed either way: there is none. */}
        {showDetails && zeroMoney.hasCredit && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              marginTop: 4,
            }}
          >
            {zeroMoney.expected != null && (
              <span
                style={{
                  fontSize: 12,
                  color: colors.text.muted,
                  ...typography.numNormal,
                }}
              >
                Expected{' '}
                {currencyFormatter.safeFormat(String(zeroMoney.expected), '—')}
              </span>
            )}
            <span
              style={{
                fontSize: 12,
                color: colors.text.muted,
                ...typography.numNormal,
              }}
            >
              Explicit credit −
              {currencyFormatter.safeFormat(
                String(zeroMoney.credited),
                '$0.00'
              )}
            </span>
            {zeroMoney.netDue != null && (
              <span
                style={{
                  fontSize: 12,
                  color: colors.text.muted,
                  ...typography.numNormal,
                }}
              >
                Net due{' '}
                {currencyFormatter.safeFormat(String(zeroMoney.netDue), '—')}
              </span>
            )}
          </div>
        )}
      </td>
    )
  }

  const totalPaid = total?.totalPaid ?? 0
  const expected = total?.expected ?? null
  // The Expected column already shows the benefit's default expected; only
  // surface it in the cell when this month's expected DIFFERS (a per-period
  // override) — otherwise it's redundant noise.
  const rowDefaultExpected = row.carrierExpected ?? row.surecoExpected
  const showCellExpected =
    expected != null &&
    (rowDefaultExpected == null ||
      Math.abs(expected - rowDefaultExpected) > 0.005)
  // Explicit credits reduce what the member still owes without touching the
  // expected price they were invoiced on. `expected` above therefore stays as
  // the server sends it, and a credited month gains a second line reading the
  // number the cell's reconciled/anomaly state is actually derived from — so
  // the strip and the Adjust drawer can't tell the operator different stories.
  // Gated on its own condition: showCellExpected is false whenever the month's
  // expected equals the row default, which is the common case.
  // One predicate, shared with the Adjust drawer's summary panel, so the strip
  // and the drawer can't disagree about whether a month is credited. The cell
  // has no credit rows of its own — `credited` is the server's batched sum for
  // exactly those rows.
  const { credited, netDue, hasCredit } = computePeriodMoneySummary({
    totalPaid,
    expected,
    creditAmounts: total?.credited ? [total.credited] : [],
    netDue: total?.netDue,
  })
  const showCellNetDue = hasCredit && netDue != null
  // Anomaly + resolution come from the materialized store (issueType /
  // issueResolved) — the single source of truth shared with the drawer — NOT
  // re-derived from status or a live paid-vs-expected diff.
  const isAnomaly = total?.issueType != null
  const resolvedAnomaly = isAnomaly && !!total?.issueResolved
  // An open manual-review flag makes the cell read as an issue (red bg) even
  // when paid reconciles with expected — mirrors the drawer's flag state.
  const manualFlagged = !!total?.manualFlagged
  const warn = (isAnomaly && !resolvedAnomaly) || manualFlagged
  const multi = payments.length > 1
  // Server-computed per-cell flag: any history/event for this benefit-month.
  // Full history lives in the Activity drawer.
  const hasActivity = !!total?.hasActivity
  const sources = Array.from(
    new Set(payments.map((p) => p.source).filter((s): s is string => !!s))
  )
  // Latest transaction date across this cell's payments (ISO strings sort chronologically).
  const txDates = payments
    .map((p) => p.transactionDate)
    .filter((d): d is string => !!d)
    .sort()
  const latestTxDate = txDates.length > 0 ? txDates[txDates.length - 1] : null
  const amountColor = warn ? colors.status.underpaid.fg : colors.text.primary
  // Refund slices per source — the source pill gets an "(R)" marker.
  const sourceRefunds = (source: string) =>
    summarizeCellRefunds(payments.filter((p) => p.source === source))

  return (
    <td
      className={`${styles.mcol} ${styles.mv}${warn ? ` ${styles.warn}` : ''}${
        outOfRange ? ` ${styles.pastCovered}` : ''
      }`}
      title={
        outOfRange ? `${coverageTitle} — adjust payment` : 'Adjust payment'
      }
      style={{ cursor: 'pointer' }}
      onClick={() => onAdjust(row, periodKey)}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: 2,
          height: '100%',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: paymentType ? 'space-between' : 'flex-end',
            width: '100%',
            gap: 4,
          }}
        >
          {/* Payment type (Auto / Manual / Bot) pinned to the cell's top-left. */}
          {paymentType && <PaymentTypeChip type={paymentType} />}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <button
              type="button"
              className={`${styles.cellActivityBtn} ${styles['cell-history']}${
                hasActivity ? ` ${styles['has-activity']}` : ''
              }`}
              title="Activity log for this month"
              onClick={(e) => {
                e.stopPropagation()
                onOpenActivity({
                  benefitId: row.benefitId,
                  benefitLabel,
                  month: periodKey,
                })
              }}
            >
              <HistorySvg size={13} />
            </button>
            {/* Multi-txn "count" mode: N× badge next to the month total. */}
            {multi && <CountBadge>{payments.length}×</CountBadge>}
            <span
              style={{
                fontSize: 14,
                lineHeight: '20px',
                fontWeight: typography.weight.semibold,
                color: amountColor,
                ...typography.numNormal,
              }}
            >
              {currencyFormatter.safeFormat(String(totalPaid), '$0.00')}
            </span>
          </div>
        </div>

        {/* Latest transaction date in this cell (shown with details). */}
        {showDetails && latestTxDate && (
          <span
            style={{
              fontSize: 12,
              color: colors.text.muted,
              ...typography.numNormal,
            }}
          >
            {dayjs(latestTxDate).format('MMM D, YYYY')}
          </span>
        )}

        {showDetails && showCellExpected && (
          <span
            style={{
              fontSize: 12,
              color: colors.text.muted,
              marginTop: 4,
              ...typography.numNormal,
            }}
          >
            Expected {currencyFormatter.safeFormat(String(expected), '—')}
          </span>
        )}

        {/* The credit itself, spelled out rather than left implicit in the gap
            between Expected and Net due — the strip is where an operator scans
            a year at a time, and "why is net due lower" should not require
            opening the drawer to answer. Gated on hasCredit alone, not on
            showCellNetDue: a period can carry credit rows while no expected
            resolves, and the credit is still real money owed. */}
        {showDetails && hasCredit && (
          <span
            style={{
              fontSize: 12,
              color: colors.text.muted,
              marginTop: showCellExpected ? 0 : 4,
              ...typography.numNormal,
            }}
          >
            Explicit credit −
            {currencyFormatter.safeFormat(String(credited), '$0.00')}
          </span>
        )}

        {showDetails && showCellNetDue && (
          <span
            title={`Expected ${currencyFormatter.safeFormat(
              String(expected),
              '—'
            )} less ${currencyFormatter.safeFormat(
              String(credited),
              '$0.00'
            )} credited`}
            style={{
              fontSize: 12,
              color: colors.text.muted,
              // The credit line always precedes this one (showCellNetDue
              // implies hasCredit), so it owns the gap above the group.
              marginTop: 0,
              ...typography.numNormal,
            }}
          >
            Net due {currencyFormatter.safeFormat(String(netDue), '—')}
          </span>
        )}

        {/* Source pills pinned to the bottom of the cell (margin-top:auto) so
            they align horizontally across every cell in the row. */}
        {showDetails && sources.length > 0 && (
          <span
            style={{
              marginTop: 'auto',
              paddingTop: 4,
              display: 'inline-flex',
              gap: 4,
            }}
          >
            {sources.map((s) => {
              const label = PROVIDER_PILL[s]?.label ?? s.charAt(0).toUpperCase()
              const refund = sourceRefunds(s)
              const title = refund.hasRefund
                ? `${s} — includes refund ${currencyFormatter.safeFormat(
                    String(refund.refundTotal),
                    '$0.00'
                  )} (${refund.refundCount} txn${
                    refund.refundCount > 1 ? 's' : ''
                  })`
                : s
              return (
                <ProviderPill
                  key={s}
                  title={title}
                  variant={providerVariant(s)}
                >
                  {label}
                  {refund.hasRefund && <RefundMark />}
                </ProviderPill>
              )
            })}
          </span>
        )}
      </div>
    </td>
  )
}

export default MonthCell
